import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { ExclamationTriangleIcon, PlusIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { QueryClient } from "@tanstack/react-query";
import { ReactNode, useState } from "react";

import {
  AI_GENERATED,
  Button,
  Folder,
  FolderExplorer,
  GoalBankPanel,
  Label,
  Small,
  Tooltip,
  UserType,
} from "@fronterahealth/frontera-ui-components";

import {
  AddSuggestedReportGoalsMutationVariables,
  ApiTreatmentPlanTemplateAiGenerationConfigChoices,
  CatalightGoalStatus,
  ReportGoalBankType,
  ReportGoalType,
  ReportGoalTypeEdge,
  ShortTermTimelineEstimationEnums,
  useAddSuggestedReportGoalsMutation,
  useCreateUpdateReportGoalMutation,
  useGoalBankGoalsQuery,
} from "@api/graphql/types-and-hooks";
import { AddShortTermGoalsBankCard } from "@components/GoalsBank/AddShortTermGoalsBank/AddShortTermGoalsBankCard";
import { GoalBankShortTermGoalsSaveToFolder } from "@components/GoalsBank/SavedGoals/ShortTermGoals/GoalBankShortTermGoalsSaveToFolder";
import { DeleteFolderDialog } from "@components/GoalsBank/dialogs/DeleteFolderDialog";
import { useGoalBankFolders } from "@components/GoalsBank/hooks/useGoalBankFolders";
import {
  findFolder,
  getAiGeneratedFolderSection,
  getOrgFolderSection,
  getUserFolderSection,
} from "@components/GoalsBank/utils";
import { convertDBString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";

interface AddShortTermGoalBankProps {
  onAdd: () => void;
  reportId: string;
  aiGoals: ReportGoalTypeEdge[];
  reportDomainId: string | undefined;
  learnerId: string;
  isAiGoalsLoading?: boolean;
  isAiGoalsFailed?: boolean;
  isCatalightTemplate?: boolean;
  isGoalBankSuggestionsEnabled?: boolean;
  aiGenerationConfig?: ApiTreatmentPlanTemplateAiGenerationConfigChoices;
  /** Optional custom trigger button. When provided, used instead of the default "Suggested Goal" button. */
  modalButton?: ReactNode;
  assignedGoalBankGoalIds?: string[];
}

interface SuggestionsHeaderProps {
  isGoalBankSuggestionsEnabled?: boolean;
  aiGenerationConfig?: ApiTreatmentPlanTemplateAiGenerationConfigChoices;
}

const SuggestionsHeader: React.FC<SuggestionsHeaderProps> = ({ isGoalBankSuggestionsEnabled, aiGenerationConfig }) => {
  const hasAiGeneration =
    !!aiGenerationConfig && aiGenerationConfig !== ApiTreatmentPlanTemplateAiGenerationConfigChoices.NoAiGeneration;
  const tooltipText =
    isGoalBankSuggestionsEnabled && hasAiGeneration
      ? "A curated mix of goals generated by Frontera's AI and goals sourced from your organization's library, both surfaced based on this learner's assessment findings."
      : isGoalBankSuggestionsEnabled
        ? "The most relevant goals from your organization's library, surfaced based on this learner's assessment findings."
        : "Goals generated by Frontera's AI using this learner's assessment findings.";
  return (
    <div className="shrink-0 flex items-center gap-1.5 px-4 pt-3 pb-1 bg-limestone-50">
      <Label size="large" displayType="loud" colorType="primary">
        Suggestions
      </Label>
      <Tooltip text={tooltipText} />
    </div>
  );
};

export const AddShortTermGoalsBank: React.FC<AddShortTermGoalBankProps> = ({
  aiGoals,
  reportId,
  onAdd,
  learnerId,
  reportDomainId,
  isAiGoalsLoading,
  isAiGoalsFailed,
  isGoalBankSuggestionsEnabled,
  aiGenerationConfig,
  modalButton: modalButtonProp,
  assignedGoalBankGoalIds = [],
}) => {
  const [queryClient] = useState(() => new QueryClient());
  const { user } = useAuth0();
  const { isCatalightUser } = useAdminData();
  const userType: UserType = !!user?.org_id ? "organization" : "selfServe";
  const orgName = `${user![config.auth0Audience + "/orgName"] ?? ""}`;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { userFolders, orgFolders, createNewFolder, deleteFolder, editFolder } = useGoalBankFolders(searchTerm, true);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [isSaveToFolderDialogOpen, setSaveToFolderDialogOpen] = useState<boolean>(false);
  const [selectedAiGoalIds, setSelectedAiGoalIds] = useState<string[]>([]);
  const [selectedGoals, setSelectedGoals] = useState<ReportGoalBankType[]>([]); // Saves selections as full objects to enable selection across folders
  const addSuggestedShortGoalsMutation = useAddSuggestedReportGoalsMutation({});
  const createUpdateReportGoalMutation = useCreateUpdateReportGoalMutation({});
  const [deleteFolderId, setDeleteFolderId] = useState<string>("");
  const [openDeleteFolderDialog, setDeleteFolderDialogOpen] = useState(false);

  const goalBankGoals = useGoalBankGoalsQuery(
    { folderId: selectedFolderId, searchTerm },
    {
      queryKey: ["short-term-goals-" + selectedFolderId + "search-term-" + searchTerm],
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      refetchInterval: 100000,
      enabled: !!selectedFolderId && selectedFolderId !== AI_GENERATED,
    },
  );

  const onSelectedFolder = (folder: Folder) => {
    setSelectedFolderId(folder.id);
  };

  const filteredAiGoals = searchTerm
    ? aiGoals.filter(
        (goal) =>
          goal.node?.goalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          goal.node?.masteryCriteria?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : aiGoals;
  const userFoldersSection = getUserFolderSection(userFolders);
  const orgFoldersSection = getOrgFolderSection(orgFolders, orgName);
  const aiSuggestedFoldersSection = getAiGeneratedFolderSection(filteredAiGoals.length);

  const goalsToAdd = (): ReactNode[] => {
    if (selectedFolderId === AI_GENERATED) {
      if (isAiGoalsLoading) {
        return [
          <div key="ai-loading-state" className="w-full">
            <div className="flex flex-col items-center justify-center p-8 text-center bg-white border border-limestone-200 rounded-lg">
              <div className="flex items-center gap-3 mb-6 animate-pulse">
                <SparklesIcon className="h-8 w-8 text-blurple-500" />
                <span className="text-limestone-700 font-medium">AI is generating goals...</span>
              </div>
              <div className="space-y-3 w-full max-w-md">
                <div className="animate-pulse">
                  <div className="bg-limestone-200 h-16 rounded-lg"></div>
                </div>
                <div className="animate-pulse">
                  <div className="bg-limestone-200 h-16 rounded-lg"></div>
                </div>
                <div className="animate-pulse">
                  <div className="bg-limestone-200 h-16 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>,
        ];
      }

      if (isAiGoalsFailed) {
        return [
          <div key="error-state" className="w-full">
            <div className="flex flex-col items-center justify-center p-8 text-center bg-white border border-limestone-200 rounded-lg">
              <ExclamationTriangleIcon className="h-12 w-12 text-rio-600" />

              <h3 className="text-lg font-medium text-limestone-900 mb-2">AI Goals Generation Failed</h3>
              <p className="text-limestone-500">
                There was an error generating AI-suggested goals. You can still add goals from your saved folders or
                create custom goals.
              </p>
            </div>
          </div>,
        ];
      }

      const sortedAiGoals = [...filteredAiGoals].sort((a, b) => {
        const aHasBank = !!a?.node?.goalBank;
        const bHasBank = !!b?.node?.goalBank;
        if (aHasBank && !bHasBank) return -1;
        if (!aHasBank && bHasBank) return 1;
        return 0;
      });

      return sortedAiGoals.length
        ? sortedAiGoals
            .map((edge) => {
              const node = edge?.node as ReportGoalType;
              return node ? (
                <AddShortTermGoalsBankCard
                  key={node.id}
                  id={node.id}
                  goalName={node.goalName}
                  subtitle={
                    isCatalightUser && node.assessmentToolSource
                      ? "Assessment Tool Source: " + node.assessmentToolSource
                      : ""
                  }
                  description={node.masteryCriteria}
                  selected={selectedAiGoalIds.includes(node.id)}
                  suggestionType={node.aiGenerated && !node.goalBank ? "ai" : undefined}
                  onClick={() => {
                    if (selectedAiGoalIds.includes(node.id)) {
                      setSelectedAiGoalIds([...selectedAiGoalIds.filter((id) => id != node?.id)]);
                      return;
                    }
                    setSelectedAiGoalIds([...selectedAiGoalIds, node.id]);
                  }}
                />
              ) : null;
            })
            .filter(Boolean)
        : !isAiGoalsLoading && !isAiGoalsFailed
          ? [
              <div key="no-ai-goals" className="w-full">
                <div className="flex flex-col items-center justify-center p-8 text-center bg-white border border-limestone-200 rounded-lg">
                  <p className="text-limestone-500">
                    No AI-generated goals available. Try adding goals from your saved folders or create custom goals.
                  </p>
                </div>
              </div>,
            ]
          : [];
    }

    return (
      goalBankGoals.data?.getGoalBankGoals?.edges
        .map((edge) => {
          const node = edge?.node;
          return node && !assignedGoalBankGoalIds.includes(node.id) ? (
            <AddShortTermGoalsBankCard
              key={node.id}
              id={node.id}
              goalName={node.goalName}
              description={node.masteryCriteria}
              selected={selectedGoals.some((goal) => goal.id === node.id)}
              onClick={() => {
                if (selectedGoals.some((goal) => goal.id === node.id)) {
                  setSelectedGoals(selectedGoals.filter((goal) => goal.id !== node?.id));
                  return;
                }
                setSelectedGoals([...selectedGoals, node as ReportGoalBankType]);
              }}
            />
          ) : null;
        })
        .filter(Boolean) || []
    );
  };

  const addGoals = async () => {
    if (selectedGoals.length > 0 || selectedAiGoalIds.length > 0) {
      try {
        if (selectedAiGoalIds.length > 0) {
          const mutationVariables: AddSuggestedReportGoalsMutationVariables = {
            reportId: reportId || "<missing-report-id>",
            aiSuggestedGoalIds: selectedAiGoalIds,
          };

          await addSuggestedShortGoalsMutation.mutateAsync(mutationVariables);
        }

        if (selectedGoals.length > 0) {
          for (const goalBankGoal of selectedGoals || []) {
            if (goalBankGoal) {
              await createUpdateReportGoalMutation.mutateAsync({
                goalInput: {
                  goalName: goalBankGoal.goalName ?? "",
                  description: goalBankGoal.description,
                  programGoal: goalBankGoal.programGoal,
                  masteryCriteria: goalBankGoal.masteryCriteria,
                  timelineEstimationType: goalBankGoal.timelineEstimationType
                    ? (convertDBString(goalBankGoal.timelineEstimationType) as ShortTermTimelineEstimationEnums)
                    : null,
                  timelineEstimationValue: goalBankGoal.timelineEstimationValue,
                  reportId,
                  reportDomainId:
                    reportDomainId && reportDomainId !== "<missing-report-domain-id>" ? reportDomainId : undefined,
                  goalBankId: goalBankGoal.id,
                  hasMedicalNecessity: goalBankGoal.hasMedicalNecessity,
                  medicalNecessityCriteria: goalBankGoal.medicalNecessityCriteria,
                  assessmentToolSource: goalBankGoal.assessmentToolSource,
                  summaryReviewed: false,
                  status: CatalightGoalStatus.NewGoal,
                },
                learnerId: learnerId ? learnerId : "<missing-learner-id>",
              });
            }
          }
        }

        await onAdd();
        notifySuccess("Successfully Added Goal(s)");
      } catch (err) {
        notifyError("Failed To Add Goal(s)");
        console.error("Error adding goals", err);
      } finally {
        setSelectedAiGoalIds([]);
        setSelectedGoals([]);
      }
    }
  };

  const showDeleteFolderDialog = (folderId: string) => {
    setDeleteFolderId(folderId);
    setDeleteFolderDialogOpen(true);
  };

  return (
    <>
      <FolderExplorer
        userType={userType}
        selectedFolderId={selectedFolderId}
        onDeleteFolder={(folderId) => {
          showDeleteFolderDialog(folderId);
        }}
        onEditFolder={(id, newValue) => editFolder(findFolder(id, userFolders.concat(orgFolders)), newValue)}
        onCreateFolder={async (folderName, folderType) => {
          const newFolder = await createNewFolder(folderName, folderType);
          setSelectedFolderId(newFolder.id);
        }}
        onSelectedFolder={onSelectedFolder}
        onSearch={(value) => {
          setSearchTerm(value);
        }}
        showSearch={true}
        title={"Add Short Term Goals"}
        folderSections={
          orgName
            ? [aiSuggestedFoldersSection, userFoldersSection, orgFoldersSection]
            : [aiSuggestedFoldersSection, userFoldersSection]
        }
        modalButton={
          modalButtonProp ?? (
            <Button
              text={
                <Small className={`flex items-center px-2`}>
                  <PlusIcon className="mr-2 h-4 w-4" />
                  Suggested Goal
                </Small>
              }
              id="suggested-goal-btn"
              dataTestID="suggested-goal-btn"
              variant="secondary"
              buttonStyle="border"
              className="w-max min-w-[unset] max-w-[unset]"
            />
          )
        }
        footer={{
          addButtonText: "Add Goal(s)",
          onAdd: addGoals,
          onSaveToFolder: () => setSaveToFolderDialogOpen(true),
          displaySaveToFolderButton: selectedFolderId === AI_GENERATED,
        }}
        panel={
          <div className="flex flex-col h-full">
            {selectedFolderId === AI_GENERATED && (
              <SuggestionsHeader
                isGoalBankSuggestionsEnabled={isGoalBankSuggestionsEnabled}
                aiGenerationConfig={aiGenerationConfig}
              />
            )}
            <div className="flex-1 min-h-0">
              {(() => {
                const goals = goalsToAdd();
                return (
                  <GoalBankPanel
                    userType={userType}
                    isLoading={goalBankGoals.isLoading || goalBankGoals.isFetching}
                    key={goals.length}
                    noFolders={false}
                    goalsToDisplay={goalBankGoals.isLoading || goalBankGoals.isFetching ? [] : goals}
                    goalsTabs={[]}
                    onCreateFolder={() => {}}
                    noGoalsEmptyState={{
                      header:
                        selectedFolderId == AI_GENERATED
                          ? isAiGoalsLoading
                            ? ""
                            : isAiGoalsFailed
                              ? "AI Goals Generation Failed"
                              : "No AI-Generated Items Available"
                          : "Nothing to Show",
                      subHeader:
                        selectedFolderId == AI_GENERATED
                          ? isAiGoalsLoading
                            ? ""
                            : isAiGoalsFailed
                              ? "There was an error generating AI goals. You can still add saved items from folders or create custom goals."
                              : "You may add saved items from the folders on the left, or add custom items on the previous screen."
                          : "Save items to this folder for later use.",
                    }}
                  ></GoalBankPanel>
                );
              })()}
            </div>
          </div>
        }
      >
        <GoalBankShortTermGoalsSaveToFolder
          open={isSaveToFolderDialogOpen}
          shortTermGoals={[]}
          aiShortTermGoals={
            aiGoals
              .filter((aiGoal) => !!aiGoal && selectedAiGoalIds.includes(aiGoal.node?.id ?? ""))
              .map((edge) => edge.node) as ReportGoalType[]
          }
          onCloseClick={() => {
            setSaveToFolderDialogOpen(false);
            setSelectedAiGoalIds([]);
          }}
          refetch={async () => await queryClient.refetchQueries()}
        />

        <DeleteFolderDialog
          open={openDeleteFolderDialog}
          folderName={findFolder(deleteFolderId, userFolders.concat(orgFolders)).name}
          onCancelClick={() => {
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
          onDeleteClick={() => {
            if (deleteFolderId == selectedFolderId) {
              setSelectedFolderId("");
            }
            deleteFolder(findFolder(deleteFolderId, userFolders.concat(orgFolders)));
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
        />
      </FolderExplorer>
    </>
  );
};
